import React, { useImperativeHandle, useMemo, useRef, useState } from "react";
import { SearchGroup, type SearchProviderRef } from "../index";
import { initSearchValue, filterEmptyData } from "./utils";
import InnerTable from "./inner-table";
import type { IProTableProps, IProTableRef } from "./dto";

/**
 * 高级表格组件
 */
const GWTablePro = <T extends {}>(
  props: React.PropsWithChildren<IProTableProps<T>>,
  ref: React.ForwardedRef<IProTableRef>
) => {
  const {
    columns = [],
    searchConfig,
    pagination = { pageSize: 10, current: 1 },
    defaultSearchValue,
    fetchPage,
    searchGroupOptions,
    ...rest
  } = props;
  const searchGroupRef = useRef<SearchProviderRef<IReqPage>>();

  /** 控制tbale-key对table部分进行强制刷新 */
  const [tableKey, setTableKey] = useState(Math.random());
  /** 控制columns进行刷新 */
  const [columnsVersion, setColumnsVersion] = useState(0);

  /** 映射初始值 */
  const searchValue = useMemo(() => {
    return initSearchValue({ columns, searchConfig, pagination });
  }, [tableKey]);

  /** 计算初始值 */
  const defaultSearch = useMemo(() => {
    return defaultSearchValue ? defaultSearchValue(searchValue) : searchValue;
  }, [searchValue, defaultSearchValue]);

  const handleForceUpdate = (scope = "table") => {
    if (scope === "table") setTableKey(Math.random());
    if (scope === "columns") setColumnsVersion((p) => p + 1);
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        forceUpdate: (s) => handleForceUpdate(s),
        refresh: () => searchGroupRef.current?.refresh?.(),
      };
    },
    []
  );

  return (
    <SearchGroup
      key={tableKey}
      ref={searchGroupRef}
      initValue={defaultSearch}
      onSubmit={(q) => fetchPage?.(filterEmptyData(q))}
      options={{
        reset: ["current"],
        ...searchGroupOptions,
      }}
    >
      <InnerTable
        columns={columns}
        defaultSearch={defaultSearch}
        columnsVersion={columnsVersion}
        searchConfig={searchConfig}
        pagination={pagination}
        handleForceUpdate={handleForceUpdate}
        {...rest}
      />
    </SearchGroup>
  );
};

const GWTableProRef = React.forwardRef(GWTablePro) as <T extends {}>(
  props: React.PropsWithChildren<IProTableProps<T>> & {
    ref?: React.ForwardedRef<IProTableRef>;
    key?: React.Key;
  }
) => React.ReactElement<any, any> | null;

export default GWTableProRef;
